/**
 * Flow Stats
 * Row of 4 stat cards: OI, Funding, L/S Ratio, Taker Ratio
 */

import { StatCard } from './stat-card';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassOverview } from '@/hooks/use-coinglass';
import { formatCompact, formatPercent } from '@/lib/utils';
import type { Asset } from '@/stores/alert-store';

interface FlowStatsProps {
  asset: Asset;
}

function getFundingColor(rate: number): 'bullish' | 'bearish' | 'warning' | 'default' {
  const abs = Math.abs(rate);
  if (abs > 0.05) return rate > 0 ? 'bearish' : 'bullish';
  if (abs > 0.01) return 'warning';
  return 'default';
}

export function FlowStats({ asset }: FlowStatsProps) {
  const { data, isLoading } = useCoinglassOverview(asset);

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <LoadingSkeleton key={i} lines={2} />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const aggregatedOI = data.oi?.['aggregated'];
  const fundingEntries = data.funding ? Object.values(data.funding) : [];
  const avgFunding = fundingEntries.length > 0
    ? fundingEntries.reduce((sum, f) => sum + f.rate, 0) / fundingEntries.length
    : 0;
  const ls = data.longShortRatio;
  const taker = data.aggregatedTakerFlow;

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard
        title="Open Interest"
        value={aggregatedOI ? `$${formatCompact(aggregatedOI.value)}` : 'N/A'}
        subValue={aggregatedOI ? formatPercent(aggregatedOI.change24h) : undefined}
        color={aggregatedOI && aggregatedOI.change24h > 0 ? 'bullish' : aggregatedOI && aggregatedOI.change24h < 0 ? 'bearish' : 'default'}
      />
      <StatCard
        title="Funding Rate"
        value={avgFunding !== 0 ? formatPercent(avgFunding * 100, 4) : 'N/A'}
        subValue={fundingEntries.length > 0 ? `${fundingEntries.length} exchanges` : undefined}
        color={getFundingColor(avgFunding * 100)}
      />
      <StatCard
        title="L/S Ratio"
        value={ls ? `${(ls.longPct ?? 0).toFixed(1)}% / ${(ls.shortPct ?? 0).toFixed(1)}%` : 'N/A'}
        subValue={ls ? `Ratio: ${(ls.ratio ?? 0).toFixed(2)}` : undefined}
        color={ls ? ((ls.longPct ?? 0) > 55 ? 'bullish' : (ls.shortPct ?? 0) > 55 ? 'bearish' : 'default') : 'default'}
      />
      <StatCard
        title="Taker Ratio"
        value={taker ? `${((taker.ratio ?? 0) * 100).toFixed(1)}% buy` : 'N/A'}
        subValue={taker ? `Delta: $${formatCompact(taker.delta)}` : undefined}
        color={taker ? ((taker.ratio ?? 0) > 0.55 ? 'bullish' : (taker.ratio ?? 0) < 0.45 ? 'bearish' : 'default') : 'default'}
      />
    </div>
  );
}
